import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { UserV1 } from 'models';
import { usePermissions } from 'models/user/permissions';
import { WeezHeader, Icon, Card, Button, ContentCard, Table, TableCellDropdown, Dropdown, ToastContext } from '@weezevent/nacre';
import { fetcher } from '@weezevent/weezjs-core';

import { listSelection, requestCount } from 'utils/utils';
import NewUser from './user/NewUser';
import { LoadingTable, Paginator, SauronEmptyCard, PaginationDropDown, QuickFilters } from 'components';
import RoleCreationModal from './user/roles/RoleCreationModal';

import css from './list.module.css';
import useQueryFilters from '../../hooks/useQueryParams/useQueryFilters';
import UseApiCallParams from '../../hooks/useQueryParams/useApiCallParams';
import CustomFilterSetController from '../../components/CustomFilterSetController';

const POSSIBLE_PER_PAGE_COUNT = [10, 50, 100];
const EMPTY_OBJECT = {};

export const UsersList = ({ organizationId = null, useCase }) => {
    const [resourceKey, resourceKey_singular] = React.useMemo(() => [useCase, useCase === 'applications' ? 'application' : 'user'], [useCase]);
    const history = useHistory();
    const { t } = useTranslation();
    const user = usePermissions();
    const toast = React.useContext(ToastContext);
    const [userRoleModal, setUserRoleModal] = React.useState({ user: null, open: false });
    const [usersIds, setUsersIds] = React.useState([]);
    const [selectedUsers, setSelectedUsers] = React.useState([]);
    const [newUserModal, setNewUserModal] = React.useState(false);

    const params = useQueryFilters(10, POSSIBLE_PER_PAGE_COUNT);
    const apiCallParams = UseApiCallParams(params.appliedFilters, EMPTY_OBJECT);

    const [users, loadingUsers, syncUsers] = UserV1.useApiModel(
        {
            cache: false,
            allow_cache: false,
            organizationId,
            useCase: useCase,
            query: {
                limit: params.perPageCount,
                offset: params.offset,
                ...apiCallParams
            },
            launch: params.initialized,
            onSuccess: response => {
                setUsersIds(response.map(user => user.username));
            },
            onError: () => {
                toast.error(t('sauron.toasts.error'));
            }
        },
        [apiCallParams, organizationId, useCase]
    );

    const usersCount = React.useMemo(() => {
        return requestCount(loadingUsers, users);
    }, [users, loadingUsers, requestCount]);

    const handleQuickFilter = React.useCallback(
        checked => {
            params.setFilters({ ...params.filters, same_level_users: checked });
        },
        [params]
    );

    const handleSelect = React.useCallback(
        user => {
            setSelectedUsers(listSelection(selectedUsers, usersIds, user));
        },
        [selectedUsers, usersIds, setSelectedUsers]
    );
    return (
        <>
            <WeezHeader
                rightComponent={[<Button key={0} primary icon={<Icon name={'plus'} />} label={t(`sauron.${resourceKey}.add`)} onClick={() => setNewUserModal(true)} />]}
                title={t(`common.${resourceKey}`)}
                backLink={
                    organizationId && {
                        onClick: () => {
                            history.push(`/organizations`);
                        }
                    }
                }
            >
                {t(`sauron.${resourceKey}.list`)}
            </WeezHeader>

            <Card>
                <ContentCard>
                    <CustomFilterSetController
                        total={usersCount}
                        labelTotal={t(`sauron.${resourceKey}.${resourceKey_singular}`, { count: usersCount })}
                        onChange={() => {}}
                        search={{
                            onChange: (_, value) => {
                                params.setSearch(value);
                            },
                            handleReset: () => {
                                params.setSearch('');
                            },
                            placeholder: t('common.search'),
                            value: params.search
                        }}
                    />
                    <PaginationDropDown
                        title={t(`sauron.${resourceKey}.${resourceKey_singular}_action`, { count: selectedUsers.length })}
                        tooltip={t(`sauron.${resourceKey}.no_action_tooltip`)}
                        label={`sauron.${resourceKey}.page_size`}
                        selected={selectedUsers}
                        count={usersCount}
                        disabled
                        limit={params.perPageCount}
                        pageSizes={POSSIBLE_PER_PAGE_COUNT}
                        onChange={value => {
                            params.setPerPageCount(value);
                        }}
                    />
                    {!organizationId && user.isAdmin() && useCase === 'users' && (
                        <QuickFilters>
                            <QuickFilters.Item label={t(`sauron.${resourceKey}.quick-filter`)} active={params.appliedFilters.same_level_users} onToggle={handleQuickFilter} />
                        </QuickFilters>
                    )}
                </ContentCard>
                {newUserModal && (
                    <NewUser organizationId={organizationId} useCaseItems={[resourceKey, resourceKey_singular]} open={newUserModal} onClose={() => setNewUserModal(false)} />
                )}
                <Table useNew selectable onSelectAll={() => handleSelect()} className={css['users-table']}>
                    <Table.THead>
                        <Table.Tr isHeader active={usersIds.length > 0 && usersIds.every(id => selectedUsers.includes(id))}>
                            <th className={css['users-table-medium']}>{t(`common.${resourceKey_singular}`)}</th>
                            <th className={css['users-table-medium']}>{t(`common.email`)}</th>
                            <th className={css['users-table-medium']}>{t(`common.roles`)}</th>
                            <th className={css['users-table-small']}>{t(`common.actions`)}</th>
                        </Table.Tr>
                    </Table.THead>
                    <Table.TBody>
                        {!loadingUsers &&
                            users?.length > 0 &&
                            users?.map(_user => (
                                <UserItem
                                    key={_user.username}
                                    authenticatedUser={user}
                                    user={_user}
                                    sync={syncUsers}
                                    selected={selectedUsers}
                                    onSelect={handleSelect}
                                    onOpen={setUserRoleModal}
                                    segment={[resourceKey, resourceKey_singular]}
                                />
                            ))}
                    </Table.TBody>
                </Table>
                <RoleCreationModal open={userRoleModal.open} user={userRoleModal.user} onClose={() => setUserRoleModal({ user: null, open: false })} sync={syncUsers} />
                <LoadingTable loading={loadingUsers} height="30vh" />
                {!loadingUsers &&
                    (users?.length > 0 ? (
                        <Paginator currentPage={params.currentPage} onPageChange={params.setCurrentPage} pageSize={params.perPageCount} totalCount={usersCount} />
                    ) : (
                        <SauronEmptyCard title={t(`sauron.${resourceKey}.empty-card-title`)} subTitle={t(`sauron.${resourceKey}.empty-card-subtitle`)} />
                    ))}
            </Card>
        </>
    );
};

const UserItem = ({ user, sync, authenticatedUser, selected, onSelect, onOpen, segment }) => {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);
    const history = useHistory();
    const [resourceKey, resourceKey_singular] = segment;
    const resolvedUser = React.useMemo(() => {
        return {
            name: user.resolvedName,
            email: user.email || '-',
            roles: user.user_roles?.filter(role => role.context.level <= 3 && !role.context.parents?.some(p => p.value !== '*')).length
        };
    }, [user]);

    const handleRedirect = React.useCallback(() => {
        history.push(`${resourceKey}/${user.id || user.username}`);
    }, [user]);

    const handleDeleteUser = React.useCallback(() => {
        const url = resourceKey === 'applications' ? `/ticket/v1/applications/${user.id}` : `/ticket/users/${user.username}`;

        fetcher
            .delete(url)
            .then(response => {
                if (response.status === 204) {
                    toast.success(t(`sauron.toasts.${resourceKey}.delete-${resourceKey_singular}-success`));
                }
            })
            .catch(() => {
                toast.error(t('sauron.toasts.error'));
            })
            .finally(() => {
                sync();
            });
    }, [user.id, user.username, resourceKey, resourceKey_singular]);

    return (
        <Table.Tr onSelected={() => onSelect(user.username)} active={selected.includes(user.username)} onClick={handleRedirect}>
            <Table.Td>{resolvedUser.name}</Table.Td>
            <Table.Td>{resolvedUser.email}</Table.Td>
            <Table.Td>{`${resolvedUser.roles} ${t(`sauron.${resourceKey}.role`, { count: resolvedUser.roles })}`}</Table.Td>

            <TableCellDropdown>
                <Dropdown.Item item={t(`sauron.${resourceKey_singular}.see-${resourceKey_singular}`)} onClick={handleRedirect} />
                <Dropdown.Item item={t(`sauron.${resourceKey_singular}.add-role`)} onClick={() => onOpen({ user, open: true })} disabled={!authenticatedUser.canManageRoles()} />
                {authenticatedUser.isAdmin() && resourceKey_singular === 'application' && (
                    <Dropdown.Item deleteItem={true} item={t(`sauron.${resourceKey_singular}.delete`)} onClick={() => handleDeleteUser()} />
                )}
            </TableCellDropdown>
        </Table.Tr>
    );
};
